import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart2,
  Settings,
  DollarSign,
  Users,
  Menu,
  X,
  LogOut,
  Moon,
  Sun,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { LucideIcon } from "lucide-react";
import { LibraryBig } from "lucide-react";
import { ScanBarcode } from "lucide-react";
import { PackagePlus } from "lucide-react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL!,
  process.env.REACT_APP_SUPABASE_ANON_KEY!
);

interface SidebarItemProps {
  icon: LucideIcon;
  text: string;
  to: string;
  darkMode: boolean;
  onClick: () => void;
  hasSubmenu?: boolean;
  isOpen?: boolean;
  toggleSubmenu?: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon: Icon,
  text,
  to,
  darkMode,
  onClick,
  hasSubmenu,
  isOpen,
  toggleSubmenu,
}) => {
  const location = useLocation();
  const isActive = location.pathname.startsWith(to);

  return (
    <li className="mb-2">
      <Link
        to={to}
        className={`flex items-center justify-between py-2 px-4 rounded ${
          isActive
            ? darkMode
              ? "bg-gray-800 text-white"
              : "bg-white text-gray-900"
            : darkMode
            ? "text-gray-200 hover:bg-gray-700"
            : "text-gray-600 hover:bg-gray-200"
        }`}
        onClick={hasSubmenu ? toggleSubmenu : onClick}
      >
        <div className="flex items-center">
          <Icon className="mr-3 h-5 w-5" />
          <span>{text}</span>
        </div>
        {hasSubmenu && (
          <div className="ml-auto">
            {isOpen ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </div>
        )}
      </Link>
    </li>
  );
};

interface SidebarProps {
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ darkMode, setDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isProductSubmenuOpen, setIsProductSubmenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const toggleProductSubmenu = () =>
    setIsProductSubmenuOpen(!isProductSubmenuOpen);

  const handleItemClick = () => {
    if (isMobile) {
      setIsOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const isDashboard = location.pathname === "/dashboard";

  return (
    <>
      <div
        className={`${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 transition-transform duration-300 ease-in-out fixed md:static inset-y-0 left-0 z-30 w-64 h-screen flex flex-col ${
          darkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        <div
          className={`p-4 border-b ${
            darkMode ? "border-gray-700" : "border-gray-200"
          } flex justify-between items-center`}
        >
          <h2
            className={`text-3xl font-semibold ${
              darkMode ? "text-white" : "text-gray-800"
            }`}
          >
            level
            <span className="text-blue-500">crm</span>
          </h2>
          <button
            className={`md:hidden p-2 rounded-md ${
              darkMode
                ? "text-gray-300 hover:bg-gray-700"
                : "text-gray-600 hover:bg-gray-200"
            }`}
            onClick={toggleSidebar}
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <nav className="flex-grow overflow-y-auto">
          <ul className="py-4">
            <SidebarItem
              icon={BarChart2}
              text="Tableau de bord"
              to="/dashboard"
              darkMode={darkMode}
              onClick={handleItemClick}
            />
            <li className="mb-2 mt-6">
              <h3
                className={`text-xs font-semibold uppercase px-4 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                LES ESSENTIELS
              </h3>
            </li>
            <SidebarItem
              icon={Users}
              text="Clients"
              to="/dashboard/clients"
              darkMode={darkMode}
              onClick={handleItemClick}
            />
            <SidebarItem
              icon={LibraryBig}
              text="Commandes"
              to="/dashboard/orders"
              darkMode={darkMode}
              onClick={handleItemClick}
            />
            <SidebarItem
              icon={ScanBarcode}
              text="Produits"
              to="/dashboard/products"
              darkMode={darkMode}
              onClick={handleItemClick}
              hasSubmenu={true}
              isOpen={isProductSubmenuOpen}
              toggleSubmenu={toggleProductSubmenu}
            />
            {isProductSubmenuOpen && (
              <ul
                className={`ml-6 mt-2 ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                <li>
                  <Link
                    to="/dashboard/products/categories"
                    className={`block py-2 px-4 rounded ${
                      darkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"
                    }`}
                    onClick={handleItemClick}
                  >
                    Catégories
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/products/sizes"
                    className={`block py-2 px-4 rounded ${
                      darkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"
                    }`}
                    onClick={handleItemClick}
                  >
                    Tailles
                  </Link>
                </li>
              </ul>
            )}

            <li className="mb-2 mt-6">
              <h3
                className={`text-xs font-semibold uppercase px-4 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                SERVICES AVANCÉS
              </h3>
            </li>
            <SidebarItem
              icon={PackagePlus}
              text="Demandes"
              to="/requests"
              darkMode={darkMode}
              onClick={handleItemClick}
            />
            <SidebarItem
              icon={DollarSign}
              text="Accounting"
              to="/dashboard/accounting"
              darkMode={darkMode}
              onClick={handleItemClick}
            />
            <SidebarItem
              icon={Settings}
              text="Paramètres"
              to="/dashboard/settings"
              darkMode={darkMode}
              onClick={handleItemClick}
            />
          </ul>
        </nav>
        <div
          className={`p-4 border-t ${
            darkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <button
            onClick={handleLogout}
            className={`flex items-center w-full py-2 px-4 rounded ${
              darkMode
                ? "text-gray-300 hover:bg-gray-700"
                : "text-gray-600 hover:bg-gray-200"
            }`}
          >
            <LogOut className="mr-3 h-5 w-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>
      {!isOpen && (
        <div
          className={`md:hidden fixed top-0 left-0 right-0 z-20 ${
            darkMode ? "bg-gray-800" : "bg-white"
          } shadow-md`}
        >
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center">
              <h2
                className={`text-3xl font-semibold ${
                  darkMode ? "text-white" : "text-gray-800"
                } mr-2`}
              >
                levelcrm
              </h2>
              <button
                className={`p-2 rounded-md ${
                  darkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-600 hover:bg-gray-200"
                }`}
                onClick={toggleSidebar}
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
            {!isDashboard && (
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-md ${
                  darkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-600 hover:bg-gray-200"
                }`}
              >
                {darkMode ? (
                  <Sun className="h-6 w-6" />
                ) : (
                  <Moon className="h-6 w-6" />
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
